// "Coming soon" for areas and features that are not live yet (replaces the plain not-set-up panel).
// It names the area, says in one line what it will do, states plainly that nothing here is live, and
// points to the closest area that works today. No dates, sign-ups or promises are shown.
import { i18n, t } from "@ady/i18n";
import type { ReactNode } from "react";
import { cx, Icon, type IconName } from "@ady/ui";
import { AppLink } from "./AppLink";
import { useShell } from "./context";
import { can, findItem, hrefFor, iconFor } from "./nav";

/** The live area to suggest from each area that is still coming. */
const RELATED: Record<string, string> = {
  reviews: "customers",
  loyalty: "customers",
  expenses: "money",
  suppliers: "money",
  statements: "money",
  links: "promotions",
  affiliates: "promotions",
  seo: "theme",
  paymentSettings: "payments",
  checkout: "storeProfile",
  region: "storeProfile",
  organization: "team",
};

export function ComingSoonBadge({ className }: { className?: string }) {
  return (
    <span
      className={cx(
        "ady-bg-brand inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-2xs font-bold tracking-wide text-on-primary uppercase",
        className,
      )}
    >
      <Icon name="sparkle" size={12} />
      {t("comingSoon.badge")}
    </span>
  );
}

/** The body: icon tile, badge, title, what it will do, the honest note and where to go meanwhile. */
export function ComingSoon({
  title,
  icon,
  about,
  related,
  art,
  hero,
  compact = false,
  headingLevel = 1,
}: {
  title: string;
  icon: IconName;
  /** Replaces the icon in the tile (the AI bot on AI areas). */
  art?: ReactNode;
  /** Replaces the whole tile with a larger piece (the 3D assistant mascot, D-057). */
  hero?: ReactNode;
  about: string;
  /** A nav item id to suggest instead. */
  related?: string;
  compact?: boolean;
  headingLevel?: 1 | 2;
}) {
  const { membership, orgSlug, storeSlug } = useShell();
  const today = findItem("today");
  const alt = related ? findItem(related) : undefined;
  const altOk = alt && can(membership, alt.anyOf) && alt.id !== "today";
  const H = headingLevel === 1 ? "h1" : "h2";
  return (
    <div
      className={cx(
        "flex flex-col items-center overflow-hidden text-center",
        !compact && "rounded-panel border border-card bg-surface",
      )}
    >
      <div
        className={cx(
          "ady-bg-brand-tint relative flex w-full justify-center",
          compact ? "rounded-panel py-8" : "py-12",
        )}
      >
        {/* Soft rings behind the tile. */}
        <span
          aria-hidden="true"
          className="absolute top-1/2 left-1/2 size-44 -translate-x-1/2 -translate-y-1/2 rounded-full border border-card"
        />
        <span
          aria-hidden="true"
          className="absolute top-1/2 left-1/2 size-64 -translate-x-1/2 -translate-y-1/2 rounded-full border border-card opacity-60"
        />
        {hero ? (
          <div className="relative w-full px-3">{hero}</div>
        ) : (
          <span className="ady-bot-host relative flex size-20 items-center justify-center rounded-panel border border-card bg-surface shadow-card">
            {art ?? <Icon name={icon} size={36} gradient />}
          </span>
        )}
      </div>
      <div className="flex max-w-xl flex-col items-center gap-3 px-6 py-8">
        <ComingSoonBadge />
        <H className="text-title-md font-bold lg:text-title-lg">{title}</H>
        <p className="text-md text-fg-secondary">{about}</p>
        <p className="flex items-center gap-1.5 text-sm text-fg-tertiary">
          <Icon name="info" size={14} />
          {t("comingSoon.notLive")}
        </p>
        {compact ? null : (
          <div className="mt-2 flex flex-wrap justify-center gap-2">
            {altOk ? (
              <AppLink
                href={hrefFor(alt, orgSlug, storeSlug)}
                className="ady-bg-brand flex min-h-control items-center gap-2 rounded-xs px-4 text-sm font-medium text-on-primary no-underline"
              >
                <Icon name={iconFor(alt)} size={16} />
                {t("comingSoon.goTo", { area: t(alt.label) })}
              </AppLink>
            ) : null}
            {today ? (
              <AppLink
                href={hrefFor(today, orgSlug, storeSlug)}
                className="flex min-h-control items-center gap-2 rounded-xs border border-control bg-surface px-4 text-sm font-medium no-underline hover:bg-hover"
              >
                <Icon name="home" size={16} />
                {t("comingSoon.backToday")}
              </AppLink>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

/** Coming soon for a nav area by id (the placeholder route). */
export function AreaComingSoon({ itemId }: { itemId: string }) {
  const item = findItem(itemId);
  if (!item) return null;
  return (
    <ComingSoon
      title={t(item.label)}
      icon={iconFor(item)}
      about={i18n.tDynamic(`comingSoon.areas.${itemId}`, "comingSoon.body")}
      related={RELATED[itemId]}
    />
  );
}
